import { useCallback } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getNotificationCount,
  getNotifications,
  patchAllNotifications,
  patchNotification,
} from '../api/react/notifications/notifications'
import type { NotificationCountFilters, NotificationFilters, UpdateNotificationRequest } from '../types/notification.types'

export const NOTIFICATION_KEYS = {
  all: () => ['notifications'] as const,
  list: (filters: NotificationFilters) => ['notifications', filters] as const,
  count: (filters: NotificationCountFilters) => ['notificationCount', filters] as const,
}

export const useNotificationsInfinite = (
  filters: Omit<NotificationFilters, 'page'>,
  cacheKey: string,
  enabled = true,
) =>
  useInfiniteQuery({
    queryKey: ['notifications', 'infinite', cacheKey, filters],
    queryFn: ({ pageParam = 1 }) => getNotifications({ ...filters, page: pageParam as number }),
    initialPageParam: 1 as number,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      if (lastPage.hasNext) return (lastPageParam as number) + 1
      return undefined
    },
    enabled,
  })

export const useNotifications = (filters: NotificationFilters, refetchInterval?: number) => {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.list(filters),
    queryFn: () => getNotifications(filters),
    placeholderData: (prev) => prev,
    refetchInterval,
  })
}

export const useNotificationCount = (filters: NotificationCountFilters, refetchInterval?: number) => {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.count(filters),
    queryFn: () => getNotificationCount(filters),
    refetchInterval,
  })
}

export const useUpdateNotification = () => {
  return useMutation({
    mutationFn: (data: UpdateNotificationRequest) => patchNotification(data),
  })
}

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => patchAllNotifications(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'infinite'] })
      queryClient.invalidateQueries({ queryKey: ['notificationCount'] })
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'infinite'] })
      queryClient.invalidateQueries({ queryKey: ['notificationCount'] })
    },
  })
}

// Floating notifications — mark single notification read with optimistic cancel
export const useMarkNotificationRead = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: patchNotification,
    onMutate: async (variables) => {
      await qc.cancelQueries({ queryKey: NOTIFICATION_KEYS.all() })
      const previousEntries = qc.getQueriesData({ queryKey: NOTIFICATION_KEYS.all() })
      return { notificationId: variables.id, previousEntries }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousEntries) {
        for (const [key, data] of context.previousEntries) {
          qc.setQueryData(key, data)
        }
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all() })
    },
  })
}

// Floating notifications — mark all read with optimistic count reset
export const useMarkAllNotificationsReadFloat = (countFilters: NotificationCountFilters) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: patchAllNotifications,
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: NOTIFICATION_KEYS.all() })
      const previousCount = qc.getQueryData(NOTIFICATION_KEYS.count(countFilters))
      qc.setQueryData(NOTIFICATION_KEYS.count(countFilters), { count: 0 })
      return { previousCount }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousCount !== undefined) {
        qc.setQueryData(NOTIFICATION_KEYS.count(countFilters), context.previousCount)
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all() })
    },
  })
}

// Notifications page — optimistic cache helpers
export const useNotificationsCacheOps = (filters: NotificationFilters) => {
  const qc = useQueryClient()
  return {
    getList: () => qc.getQueryData(NOTIFICATION_KEYS.list(filters)),
    setList: (updater: (old: any) => any) => qc.setQueryData(NOTIFICATION_KEYS.list(filters), updater),
    invalidateAll: () => qc.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all() }),
  }
}

export const useInvalidateNotifications = () => {
  const qc = useQueryClient()
  return useCallback(() => qc.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all() }), [qc])
}

// NotificationDrawer — optimistic cache helpers for infinite queries
export const useDrawerNotificationsCacheOps = (_search: string) => {
  const qc = useQueryClient()
  return {
    setUnread: (updater: (old: any) => any) =>
      qc.setQueriesData({ queryKey: ['notifications', 'infinite', 'drawer-unread-notifications'] }, updater),
    setRead: (updater: (old: any) => any) =>
      qc.setQueriesData({ queryKey: ['notifications', 'infinite', 'drawer-read-notifications'] }, updater),
    invalidateInfinite: () => {
      qc.invalidateQueries({ queryKey: ['notifications', 'infinite'] })
      qc.invalidateQueries({ queryKey: ['notificationCount'] })
    },
    invalidateCount: () => qc.invalidateQueries({ queryKey: ['notificationCount'] }),
  }
}
